// src/components/admin/ProgramaMensualImagen.js
// Hoja con el diseño descargable del Programa Mensual. Permanece fuera de
// pantalla y el módulo la convierte en una imagen PNG al pulsar "Descargar".
import React, { forwardRef, useMemo } from "react";

const formatoFecha = (fecha) =>
  new Date(fecha).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" });

const claseCulto = (culto = "") => {
  const c = culto.toLowerCase();
  if (c.includes("oraci")) return "oracion";
  if (c.includes("dominical")) return "escuela";
  if (c.includes("general")) return "general";
  return "otro";
};

const limpiar = (texto) => (texto || "").replace(/\s+/g, " ").trim();

const ESTILOS = `
  .pmi-fuera { position: absolute; left: -20000px; top: 0; pointer-events: none; }
  .pmi-hoja { width: 1860px; padding: 44px 48px 36px; background: #f1f5f9; box-sizing: border-box;
    font-family: "Segoe UI", system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif; color: #1e293b; }
  .pmi-hoja * { box-sizing: border-box; margin: 0; padding: 0; }
  .pmi-enc { display: flex; align-items: center; gap: 22px; margin-bottom: 26px; }
  .pmi-enc img { height: 78px; width: 78px; object-fit: contain; background: #fff; border-radius: 50%; padding: 6px; box-shadow: 0 2px 8px rgba(15,23,42,.12); }
  .pmi-enc h1 { font-size: 34px; font-weight: 700; letter-spacing: .2px; color: #0f172a; }
  .pmi-enc p { font-size: 16px; color: #64748b; margin-top: 4px; }
  .pmi-mes { margin-left: auto; }
  .pmi-mes span { display: inline-block; background: #4f46e5; color: #fff; font-size: 20px; font-weight: 600; padding: 9px 22px; border-radius: 999px; letter-spacing: .3px; }
  .pmi-tarjeta { background: #fff; border-radius: 16px; box-shadow: 0 4px 18px rgba(15,23,42,.08); overflow: hidden; border: 1px solid #e2e8f0; }
  .pmi-hoja table { width: 100%; border-collapse: collapse; }
  .pmi-hoja thead th { background: #1e293b; color: #f8fafc; font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 1.1px; text-align: left; padding: 16px 18px; }
  .pmi-hoja thead th.pmi-centro { text-align: center; }
  .pmi-hoja tbody td { padding: 13px 18px; font-size: 16px; border-bottom: 1px solid #eef2f7; vertical-align: middle; }
  .pmi-hoja tbody tr.pmi-inicio td { border-top: 2px solid #cbd5e1; }
  .pmi-hoja tbody tr:first-child td { border-top: none; }
  .pmi-hoja tbody tr.pmi-alt td { background: #f8fafc; }
  .pmi-hoja td.pmi-fecha { font-weight: 600; color: #334155; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .pmi-hoja td.pmi-persona { white-space: nowrap; }
  .pmi-hoja td.pmi-semana { text-align: center; background: #f5f3ff; border-left: 1px solid #e2e8f0; color: #312e81; font-weight: 500; line-height: 1.35; }
  .pmi-hoja tr.pmi-alt td.pmi-semana { background: #eef2ff; }
  .pmi-hoja td.pmi-aseo { font-weight: 600; }
  .pmi-vacio { color: #cbd5e1; }
  .pmi-pill { display: inline-block; padding: 5px 14px; border-radius: 999px; font-size: 14px; font-weight: 600; white-space: nowrap; }
  .pmi-pill.oracion { background: #e0f2fe; color: #075985; }
  .pmi-pill.escuela { background: #dcfce7; color: #166534; }
  .pmi-pill.general { background: #fef3c7; color: #92400e; }
  .pmi-pill.otro { background: #e2e8f0; color: #334155; }
  .pmi-pie { text-align: center; margin-top: 18px; font-size: 13px; color: #94a3b8; letter-spacing: .4px; }
`;

const ProgramaMensualImagen = forwardRef(function ProgramaMensualImagen(
  { filas, nombreMes, anio, nombreMiembro },
  ref
) {
  // Agrupa las filas por semana: Aseo, Cumpleaños y Aniversario se muestran
  // una sola vez por semana, en una celda combinada.
  const semanas = useMemo(() => {
    const grupos = [];
    filas.forEach((f) => {
      const ultimo = grupos[grupos.length - 1];
      if (ultimo && ultimo.semana === f.semana_index) {
        ultimo.filas.push(f);
      } else {
        grupos.push({ semana: f.semana_index, filas: [f] });
      }
    });
    return grupos;
  }, [filas]);

  const persona = (idMiembro, nombreBackend) =>
    nombreMiembro[idMiembro] || limpiar(nombreBackend);

  const vacio = <span className="pmi-vacio">—</span>;

  return (
    <div className="pmi-fuera" aria-hidden="true">
      <style>{ESTILOS}</style>
      <div className="pmi-hoja" ref={ref}>
        <div className="pmi-enc">
          <img src="/logoIBFMO.png" alt="" />
          <div>
            <h1>Programa Mensual</h1>
            <p>Iglesia Bautista Monte de los Olivos</p>
          </div>
          <div className="pmi-mes"><span>{nombreMes} {anio}</span></div>
        </div>

        <div className="pmi-tarjeta">
          <table>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Culto</th>
                <th>Director</th>
                <th>Proyección</th>
                <th className="pmi-centro">Aseo</th>
                <th className="pmi-centro">Cumpleaños</th>
                <th className="pmi-centro">Aniversario</th>
              </tr>
            </thead>
            <tbody>
              {semanas.map((grupo, i) =>
                grupo.filas.map((f, j) => {
                  const primera = grupo.filas[0];
                  const aseo = primera.aseo_familia
                    ? `${primera.aseo_familia}${primera.aseo_hermano ? ` - ${primera.aseo_hermano}` : ""}`
                    : "";
                  const cumples = (primera.cumpleanios || []).join(", ");
                  const aniversarios = (primera.aniversarios || []).join(", ");

                  return (
                    <tr key={f.id_programa} className={`${i % 2 ? "pmi-alt" : ""} ${j === 0 ? "pmi-inicio" : ""}`}>
                      <td className="pmi-fecha">{formatoFecha(f.fecha)}</td>
                      <td><span className={`pmi-pill ${claseCulto(f.culto)}`}>{f.culto}</span></td>
                      <td className="pmi-persona">{persona(f.id_miembro_director, f.director_nombre) || vacio}</td>
                      <td className="pmi-persona">{persona(f.id_miembro_proyeccion, f.proyeccion_nombre) || vacio}</td>
                      {j === 0 && (
                        <>
                          <td className="pmi-semana pmi-aseo" rowSpan={grupo.filas.length}>{aseo || vacio}</td>
                          <td className="pmi-semana" rowSpan={grupo.filas.length}>{cumples || vacio}</td>
                          <td className="pmi-semana" rowSpan={grupo.filas.length}>{aniversarios || vacio}</td>
                        </>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="pmi-pie">Monte de los Olivos</div>
      </div>
    </div>
  );
});

export default ProgramaMensualImagen;
